import { Money } from "../types/money";
import { Currency } from "../types/enums/currency";
import { Country } from "../types/enums/Country";

export const createMoney = (amount: number, currency: Currency): Money => {
  return {
    amount,
    currency: currency,
  };
};

export const addMoney = (money: Money, addend: Money | number): Money => {
  if (typeof addend === "number") {
    return {
      amount: money.amount + addend,
      currency: money.currency,
    };
  }
  return {
    amount: money.amount + addend.amount,
    currency: money.currency || addend.currency,
  };
};

export const subtractMoney = (
  money: Money,
  subtrahend: Money | number,
): Money => {
  if (typeof subtrahend === "number") {
    return {
      amount: money.amount - subtrahend,
      currency: money.currency,
    };
  }
  return {
    amount: money.amount - subtrahend.amount,
    currency: money.currency || subtrahend.currency,
  };
};

export const multiplyMoney = (money: Money, multiplier: number): Money => {
  return {
    amount: money.amount * multiplier,
    currency: money.currency,
  };
};

export const divideMoney = (money: Money, divisor: number): Money => {
  return {
    amount: money.amount / divisor,
    currency: money.currency,
  };
};

export const absMoney = (money: Money): Money => {
  return {
    amount: Math.abs(money.amount),
    currency: money.currency,
  };
};

export const roundMoney = (money: Money): Money => {
  return {
    amount: Math.round(money.amount),
    currency: money.currency,
  };
};

export const asDecimal = (money: Money): number => {
  return Math.round(money.amount) / 100;
};

export const isLessThan = (money: Money, other: Money): boolean => {
  return money.amount < other.amount;
};

export const isGreaterThan = (money: Money, other: Money): boolean => {
  return money.amount > other.amount;
};

export const isEqualTo = (money: Money, other: Money): boolean => {
  return money.amount === other.amount;
};

export const zeroMoney = (currency: Currency): Money => {
  return createMoney(0, currency);
};

export type FormatMoneyOptions = {
  includeCurrency?: boolean;
};

export const formatMoney = (
  locale: string,
  money: Money,
  options?: FormatMoneyOptions,
) => {
  const decimalValue = asDecimal(money);
  const isInteger = Number.isInteger(decimalValue);
  const includeCurrency = options?.includeCurrency ?? true;

  const PriceFormatter = new Intl.NumberFormat(locale, {
    style: includeCurrency ? "currency" : "decimal",
    currency: money.currency,
    minimumFractionDigits: isInteger ? 0 : 2,
    maximumFractionDigits: isInteger ? 0 : 2,
  });

  return PriceFormatter.format(decimalValue)
    .replace(/\u00A0/g, " ")
    .replace("−", "-");
};

export const CountryCurrency = {
  [Country.SWEDEN]: Currency.SEK,
  [Country.FINLAND]: Currency.EUR,
};
